import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import NewEncounterFormStyle from './NewEncounterForm.module';
import useAxios from '../../../hooks/useAxios';
import AuthContext from '../../../contexts/auth-context';
import { useContext, useState, useEffect, memo, useImperativeHandle, forwardRef } from 'react';
import { urls, MIN_NUMBER_OF_SEARCH, toastify_status } from '../../config/variables';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { validateIcdCode } from '../../../Utils/validateHttpResponse';
import Toastify from '../../ui/Toastify';
import MedicalSelections from './MedicalSelections';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import SearchableSelect from '../../ui/SearchableSelect/SearchableSelect';
import { GlobalStyles } from '../../../constants/GlobalStyles';
import {useTranslation} from 'react-i18next'

const DiagnoseSelect = memo(forwardRef(
  ({ diagnoseInput, handleSelectDiagnose, diagnoses, clearSearch, handleDeleteDiagnose, handleRemoveDiagnoseInput, isDisabled, favourite, updateFavourite }, ref) => {
    const { label, index, name } = diagnoseInput;
    const classes = NewEncounterFormStyle();
    const { operation } = useAxios();
    const authCtx = useContext(AuthContext);
    const [diagnoseOptions, setDiagnoseOptions] = useState([]);
    const [loadingDiagnoseOptions, setLoadingDiagnoseOptions] = useState(false);
    const [diagnoseOptionsError, setDiagnoseOptionsError] = useState(undefined);
    const [searchQuery, setSearchQuery] = useState('');
    const [prevSearchQuery, setPrevSearchQuery] = useState('');
    const [selectInputLabel, setSelectInputLabel] = useState('Please search first before selecting.');
    // favourite diagnoses
    const [favouriteDiagnoses, setFavouriteDiagnoses] = useState([])
    const [openFavouriteDiagnosesDialog, setOpenFavouriteDiagnosesDialog] = useState(false);
    // check if the selected diagnoses is a favourite diagnose or no
    const [selectedFavouriteDiagnose, setsSelectedFavouriteDiagnose] = useState(false);
    const [loadingFavourite, setLoadingFavourite] = useState(false);
    const [selectedDiagnose, setSelectedDiagnose] = useState('');
    const {t} = useTranslation('newEncounter')

    // call a function inside a child component from the parent component
    // used to populate a specific diagnoses from nlp
    useImperativeHandle(ref, () => ({
      populateDiagnose: (icd, i) => {
        if(index === i) {
          selectFavouriteDiagnose(icd); // Populate the diagnoses in the same way selecting favorite diagnoses.
          Toastify({message: `Diagnose ${icd.icdcode} is populated successfully.`, state: toastify_status.SUCCESS})
        } else {
          Toastify({message: 'Populated Failed.', state: toastify_status.ERROR})
        }
      }
    }));

    useEffect(() => {
      if(Array.isArray(favourite.icds)) {
        setFavouriteDiagnoses(favourite.icds)
        setLoadingFavourite(false)
      }
    }, [favourite])

    const handleSetDiagnoseOptions = (resDiagnoseOptions) => {
      handleDeleteSelectedDiagnose()
      setLoadingDiagnoseOptions(false)
      if(validateIcdCode(resDiagnoseOptions)) {
        setDiagnoseOptions(resDiagnoseOptions);
        setSelectedDiagnose('')
        resDiagnoseOptions.length > 0
          ? setSelectInputLabel(<Typography sx={classes.found_options}>{`Search or select from (${resDiagnoseOptions.length}) options...`}</Typography>)
          : setSelectInputLabel(<Typography sx={classes.not_found_options}>No Diagnosis found for "{searchQuery}".</Typography>);
      } else {
        console.log('invalid diagnosis response')
        Toastify({message: 'invalid diagnosis response', state: toastify_status.ERROR})
      }
    };

    const handleSubmit = () => {
      if (searchQuery.length >= MIN_NUMBER_OF_SEARCH) {
        setDiagnoseOptionsError('');
        setLoadingDiagnoseOptions(true)
        operation(
          {
            method: 'POST',
            url: urls.LIST_SEARCHED_DIAGNOSES,
            headers: { Authorization: `token ${authCtx.token}` },
            data: {"searchfor": searchQuery},
          },
          handleSetDiagnoseOptions,
          handleSetDiagnosisError
        );
      }
    };

    const handleSetDiagnosisError = (error) => {
      setLoadingDiagnoseOptions(false)
      setDiagnoseOptionsError(error)
    }

    const handleSearch = (event) => {
      const value = event.target.value;
      if (typeof value !== 'undefined') {
        setSearchQuery(value);
      }
    };

    // Note: this clearSearchQuery argument is a simple string passed over from handleClearDiagnose InputAdornment below
    const handleClearDiagnose = (clearSearchQuery) => {
      if(clearSearchQuery) {
        setSearchQuery('');
      }
      handleDeleteDiagnose(index);
      setDiagnoseOptions([]);
      setDiagnoseOptionsError(undefined);
      setSelectInputLabel('Please search first before selecting.');
      setsSelectedFavouriteDiagnose(false)
      setSelectedDiagnose('')
    }

    useEffect(() => {
      setSearchQuery('');
      setDiagnoseOptions([]);
      setDiagnoseOptionsError(undefined);
      setSelectInputLabel('Please search first before selecting.');
      setSelectedDiagnose('')
    }, [clearSearch]);

    useEffect(() => {
      setPrevSearchQuery(searchQuery)
    }, [searchQuery])

    /* use effect to clear Diagnoses in case the user removed or changed the search query
     after get diagnosee options or select diagnose */
     useEffect(() => {
      if (
        (diagnoses[index] && 
         (!searchQuery || (prevSearchQuery.length >= 3 && searchQuery.length === 2))) ||
        (diagnoseOptions.length > 0 && searchQuery.length <= 2)
      ) {
        handleClearDiagnose();
      }
    // eslint-disable-next-line
    }, [diagnoses, searchQuery, prevSearchQuery, diagnoseOptions]);

    // delete selected Diagnose after Re-search for options
    const handleDeleteSelectedDiagnose = () => {
      if(diagnoses[index]) {
        handleDeleteDiagnose(index);
      }
    }

    const handleOpenFavouriteDiagnosesPopup = () => {
      setOpenFavouriteDiagnosesDialog(true);
    };

    const handleCloseFavouriteDiagnosesPopup = () => {
      setOpenFavouriteDiagnosesDialog(false);
    };

    const selectFavouriteDiagnose = (icd) => {
      const event ={target: {name, value: icd.icdcode}}
      handleSelectDiagnose(event, index)
      setOpenFavouriteDiagnosesDialog(false)
      setSearchQuery(icd.shortdescription)
      setDiagnoseOptions([{...icd}])
      setSelectedDiagnose(icd)
    }

    // check if the selected diagnoses is a favourite diagnose or no
    useEffect(() => {
      if (diagnoses[index] && diagnoses[index].icd) {
        setsSelectedFavouriteDiagnose(favouriteDiagnoses.find((icd) => icd.icdcode === diagnoses[index].icd));
      }
    }, [diagnoses, index, favouriteDiagnoses]);

    const addFavouriteDiagnose = () => {
      const favouriteDiagnosesList = favouriteDiagnoses.map((icd) => icd.icdcode)
      const updatesDiagnosesList = [...favouriteDiagnosesList, diagnoses[index].icd]
      const data = {'icds': updatesDiagnosesList}
      updateFavourite(data)
      setLoadingFavourite(true)
    }

    const RemoveFavouriteDiagnose = () => {
      const updatesDiagnosesList = favouriteDiagnoses.map((icd) => icd.icdcode).filter((icd) => icd !== diagnoses[index].icd)
      const data = {'icds': updatesDiagnosesList}
      updateFavourite(data)
      setLoadingFavourite(true)
    }

    const selectDiagnose = (icd) => {
      setSelectedDiagnose(icd)
      const event ={target: {name, value: icd.icdcode}}
      handleSelectDiagnose(event, index)
    };

    return (
      <Box>
        <Box sx={classes.input_label_container}>
          <Box sx={classes.label_and_remove_input_box}>
            <InputLabel sx={classes.input_label}>{label}</InputLabel>
            {index !== 0 && (
              <IconButton size="small" onClick={() => handleRemoveDiagnoseInput(index)}>
                <Tooltip title="Remove Input">
                  <RemoveIcon sx={classes.remove_icon} />
                </Tooltip>
              </IconButton>
            )}
          </Box>

          <Box sx={classes.input_label_container_buttons_box}>
            <Button
              sx={classes.open_dialog_button}
              onClick={handleOpenFavouriteDiagnosesPopup}
              disabled={isDisabled || (!loadingFavourite && favouriteDiagnoses.length === 0)}
            >
              <Typography
                sx={[classes.dialog_button_title, {opacity: loadingFavourite ? GlobalStyles.opacity.half : GlobalStyles.opacity.full}]}>
                 {t("FavoriteDiagnoses")}
              </Typography>
              {loadingFavourite && (
                <CircularProgress
                  size={14}
                  sx={classes.input_label_container_button_circular_progress}
                />
              )}
            </Button>
          </Box>
        </Box>

        <Box sx={classes.select_inputs_container}>
          <TextField
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
            disabled={isDisabled}
            variant="outlined"
            InputProps={{
              startAdornment: searchQuery && (
                <InputAdornment position="start">
                  <IconButton onClick={() => handleClearDiagnose('clearSearchQuery')} sx={classes.clear_button}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSubmit} sx={classes.search_button} disabled={searchQuery.length < MIN_NUMBER_OF_SEARCH}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={searchQuery}
            onChange={handleSearch}
            placeholder={`Search for at least ${MIN_NUMBER_OF_SEARCH} characters`}
            sx={classes.search_input}
            size="small"
            error={!!diagnoseOptionsError}
            helperText={diagnoseOptionsError ? diagnoseOptionsError.message : ''}
          />

          <Box sx={classes.searchable_and_star_box_container}>
            <SearchableSelect
              value={selectedDiagnose}
              onChange={selectDiagnose}
              options={loadingDiagnoseOptions ? [] : diagnoseOptions}
              placeholder={selectInputLabel}
              disabled={isDisabled || diagnoseOptions.length === 0 || loadingDiagnoseOptions}
              getOptionLabel={(option) => `${option.icdcode} - ${option.shortdescription}`}
              getOptionValue={(option) => option.icdcode}
              loading={loadingDiagnoseOptions}
            />

            <Box sx={classes.star_box}>
              {loadingFavourite ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton
                  disabled={!diagnoses[index]}
                  onClick={selectedFavouriteDiagnose ? RemoveFavouriteDiagnose : addFavouriteDiagnose}
                >
                  <Tooltip title={selectedFavouriteDiagnose ? 'Remove From Favourites' : 'Add To Favourites'}>
                    {selectedFavouriteDiagnose ? <StarIcon /> : <StarBorderIcon />}
                  </Tooltip>
                </IconButton>
              )}
          </Box>
          </Box>

        </Box>
        <MedicalSelections
         selectionType='icd'
         title={t("FavoriteDiagnose")}
         open={openFavouriteDiagnosesDialog}
         handleClose={handleCloseFavouriteDiagnosesPopup}
         loading={false}
         data={favouriteDiagnoses}
         selectOption={selectFavouriteDiagnose}
          />
      </Box>
    );
  }
));

export default DiagnoseSelect;
